import React, { useState, useRef, useEffect } from 'react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import TrajectoryGame from './TrajectoryGame';


export default function Hero() {
  const [isFlying, setIsFlying] = useState(false);
  const [showGame, setShowGame] = useState(false);

  const [screenWidth, setScreenWidth] = useState(0);
  const buttonRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const updateWidth = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const distanceToRight = window.innerWidth - rect.left;
        setScreenWidth(distanceToRight);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  return (
    <section className="panel" id="hero">
      <Row className="align-items-center p-0 pt-4">
        {/* Coluna da Imagem */}
        <Col xs={12} md={4} className="text-center mb-4 mb-md-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            whileHover={{ scale: 1.05, rotate: 2 }}
          >
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
                borderRadius: '50%',
                padding: '0.5rem',
                background: 'linear-gradient(135deg, rgba(45, 81, 83, 0.3), rgba(0, 200, 255, 0.2))',
                boxShadow: '0 0 40px rgba(0, 245, 255, 0.4)',
                border: '3px solid rgba(0, 245, 255, 0.5)'
              }}
            >
              <motion.img
                src="/danielm.png"
                alt="Daniel Marnet"
                style={{
                  width: '250px',
                  height: '250px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '4px solid rgba(0, 0, 0, 0.3)',
                  display: 'block'
                }}
                whileHover={{
                  boxShadow: '0 0 50px rgba(0, 245, 255, 0.6)'
                }}
              />
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip
                    id="assistant-tooltip"
                    style={{
                      fontSize: '0.9rem',
                      padding: '0.5rem 0.75rem',
                      background: 'rgba(0, 0, 0, 0.9)',
                      border: '1px solid rgba(0, 245, 255, 0.5)',
                      borderRadius: '0.5rem',
                      boxShadow: '0 4px 15px rgba(0, 245, 255, 0.3)',
                      maxWidth: '250px',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }}
                    popperConfig={{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'viewport',
                            padding: 10
                          }
                        },
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 10]
                          }
                        }
                      ]
                    }}
                  >
                    {t('hero.assistantTooltip')}
                  </Tooltip>
                }
              >
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    const chatElement = document.getElementById('chat');
                    if (chatElement) {
                      chatElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    fontSize: '3rem',
                    filter: 'drop-shadow(0 0 15px rgba(0, 245, 255, 0.8))',
                    background: 'rgba(0, 0, 0, 1)',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--accent)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{
                    scale: 1.15,
                    boxShadow: '0 0 25px rgba(0, 245, 255, 1)'
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="bi bi-robot" style={{ fontSize: '1.8rem', color: 'var(--accent)' }}></i>
                </motion.div>
              </OverlayTrigger>
            </div>
          </motion.div>
        </Col>

        {/* Coluna do Texto */}
        <Col xs={12} md={8} className="text-center text-md-start">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              style={{
                fontSize: '2.8rem',
                fontWeight: '700',
                marginBottom: '1rem',
                lineHeight: '1.3',
                textShadow: '0 0 20px rgba(0, 245, 255, 0.5)'
              }}
            >
              {t('hero.title')}
              <br />
              <span style={{
                color: 'var(--accent)',
                fontSize: '2.2rem',
                display: 'block',
                marginTop: '0.5rem'
              }}>
                {t('hero.subtitle')}
              </span>
            </motion.h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <p
              style={{
                fontSize: '1.6rem',
                color: 'rgba(255, 255, 255, 0.95)',
                fontFamily: 'var(--font-body)',
                fontWeight: '500',
                marginBottom: '1.5rem',
                lineHeight: '1.6',
                letterSpacing: '0.5px'
              }}
            >
              {t('hero.name')}
              <br />
              <span style={{
                fontSize: '1.3rem',
                color: 'rgba(255, 255, 255, 0.75)',
                fontWeight: '400'
              }}>
                {t('hero.role')}
              </span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="d-flex justify-content-center justify-content-md-start"
          >
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 400 }}
              overlay={
                <Tooltip id="game-tooltip" style={{ fontFamily: 'var(--font-body)' }}>
                  {t('hero.gameTooltip')}
                </Tooltip>
              }
            >
              <motion.a
                ref={buttonRef}
                href="#about-me"
                whileHover={{
                  scale: 1.08,
                  y: -3
                }}
                whileTap={{ scale: 0.96 }}
                onClick={(e) => {
                  e.preventDefault();
                  setIsFlying(true);
                  setTimeout(() => {
                    setShowGame(true);
                    setIsFlying(false);
                  }, 800);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.95), rgba(0, 200, 255, 0.95), rgba(0, 150, 255, 0.95))',
                  border: '3px solid rgba(0, 245, 255, 0.6)',
                  padding: '1.25rem 3rem',
                  fontSize: '1.25rem',
                  fontFamily: 'var(--font-body)',
                  fontWeight: '600',
                  color: '#0a0b0d',
                  borderRadius: '3rem',
                  boxShadow: '0 8px 40px rgba(0, 245, 255, 0.5), 0 0 20px rgba(0, 245, 255, 0.3)',
                  textDecoration: 'none',
                  letterSpacing: '0.8px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'visible'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 12px 50px rgba(0, 245, 255, 0.7), 0 0 30px rgba(0, 245, 255, 0.5)';
                  e.currentTarget.style.borderColor = 'rgba(0, 245, 255, 1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 40px rgba(0, 245, 255, 0.5), 0 0 20px rgba(0, 245, 255, 0.3)';
                  e.currentTarget.style.borderColor = 'rgba(0, 245, 255, 0.6)';
                }}
              >
                <AnimatePresence>
                  {!isFlying ? (
                    <motion.span
                      key="plane-normal"
                      animate={{
                        rotate: [0, 10, -10, 0],
                        scale: [1, 1.1, 1]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatDelay: 2,
                        ease: "easeInOut"
                      }}
                      style={{
                        fontSize: '1.5rem',
                        display: 'inline-block',
                        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                      }}
                    >
                      ✈️
                    </motion.span>
                  ) : (
                    <motion.span
                      key="plane-flying"
                      initial={{
                        scale: 1,
                        rotate: 0,
                        x: 0,
                        y: 0,
                        opacity: 1
                      }}
                      animate={{
                        scale: [1, 2.5, 3],
                        rotate: [0, 20, 35, 45],
                        x: screenWidth > 0 ? [0, screenWidth * 0.25, screenWidth * 0.5, screenWidth * 0.65] : [0, 200, 400, 550],
                        y: [0, -60, -140, -220],
                        opacity: [1, 1, 0.8, 0]
                      }}
                      transition={{
                        duration: 1.5,
                        ease: "easeOut"
                      }}
                      style={{
                        fontSize: '1.5rem',
                        display: 'inline-block',
                        filter: 'drop-shadow(0 4px 8px rgba(0, 245, 255, 0.8))',
                        position: 'absolute',
                        zIndex: 1000
                      }}
                    >
                      ✈️
                    </motion.span>
                  )}
                </AnimatePresence>
                <span style={{
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                }}>
                  {t('hero.button')}
                </span>
                <motion.span
                  initial={{ x: -20, opacity: 0 }}
                  whileHover={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    fontSize: '1.2rem',
                    marginLeft: '0.5rem'
                  }}
                >
                  →
                </motion.span>
              </motion.a>
            </OverlayTrigger>
          </motion.div>
        </Col>
      </Row>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="text-center"
        style={{
          marginTop: '3rem'
        }}
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            fontSize: '1.5rem'
          }}
        >
          <i className="bi bi-arrow-down"></i>
        </motion.div>
      </motion.div>
      <AnimatePresence>
        {showGame && <TrajectoryGame onClose={() => setShowGame(false)} />}
      </AnimatePresence>
    </section >
  );
}
